import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { trpc } from "../../trpc";
import WebhookEndpointForm from "./WebhookEndpointForm";
import WebhookEventList from "./WebhookEventList";
import WebhookSecretRow from "./WebhookSecretRow";
import WebhookTestPanel from "./WebhookTestPanel";
import type { WebhookEventType, WebhookSubscription } from "./types";

const enabledEventTypes = (subscription: Pick<WebhookSubscription, "events">): WebhookEventType[] =>
  subscription.events.filter((event) => event.enabled).map((event) => event.type);

const WebhooksHeader = (): JSX.Element => (
  <div>
    <h2 className="text-2xl font-bold mb-2">Webhooks</h2>
    <p className="max-w-prose text-sm text-gray-600">
      Recibe en tu sistema los eventos de tus leads. Enviamos cada evento como un POST con JSON a tu
      URL, con tu secreto en el encabezado <code className="text-xs">X-Palomma-Secret</code> para
      que verifiques que viene de Palomma.
    </p>
  </div>
);

const WebhooksConfiguration = (): JSX.Element => {
  const utils = trpc.useUtils();
  const { data, isLoading, error } = trpc.webhooks.get.useQuery();

  const [url, setUrl] = useState("");
  const [selectedEvents, setSelectedEvents] = useState<WebhookEventType[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!data || isInitialized) return;
    setUrl(data.url ?? "");
    setSelectedEvents(enabledEventTypes(data));
    setIsInitialized(true);
  }, [data, isInitialized]);

  const update = trpc.webhooks.update.useMutation({
    onSuccess: (saved) => {
      utils.webhooks.get.setData(
        undefined,
        (prev) => prev && { ...saved, canManage: prev.canManage }
      );
      setUrl(saved.url ?? "");
      setSelectedEvents(enabledEventTypes(saved));
      toast.success("Webhook guardado");
    },
    onError: (error) => {
      if (error.data?.code === "BAD_REQUEST") return;
      toast.error("No se pudo guardar el webhook", { description: error.message });
    },
  });

  const savedEvents = useMemo(() => (data ? enabledEventTypes(data) : []), [data]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <WebhooksHeader />
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col gap-6">
        <WebhooksHeader />
        <Alert status="error">
          No pudimos cargar la configuración de webhooks. Recarga la página para intentarlo de
          nuevo.
        </Alert>
      </div>
    );
  }

  const canEdit = data.canManage && !data.managedByPalomma;
  const isDirty =
    url.trim() !== (data.url ?? "") ||
    selectedEvents.length !== savedEvents.length ||
    selectedEvents.some((type) => !savedEvents.includes(type));
  const urlError = update.error?.data?.code === "BAD_REQUEST" ? update.error.message : null;

  const testDisabledReason = !data.configured
    ? "Guarda la URL de tu webhook para enviar una prueba."
    : isDirty
      ? "Guarda tus cambios para probar la configuración nueva."
      : null;

  const handleUrlChange = (value: string) => {
    setUrl(value);
    if (update.error) update.reset();
  };

  const handleToggleEvent = (type: WebhookEventType, checked: boolean) => {
    setSelectedEvents((prev) =>
      checked
        ? [...prev.filter((candidate) => candidate !== type), type]
        : prev.filter((candidate) => candidate !== type)
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit || !isDirty || !url.trim()) return;
    update.mutate({ url: url.trim(), events: selectedEvents });
  };

  return (
    <div className="flex flex-col gap-6">
      <WebhooksHeader />

      {data.managedByPalomma && (
        <Alert status="info">
          La integración de webhooks de esta cuenta la administra Palomma. Si necesitas cambiarla,
          escríbenos.
        </Alert>
      )}
      {!data.managedByPalomma && !data.canManage && (
        <Alert status="info">Solo los administradores pueden cambiar esta configuración.</Alert>
      )}

      <form
        noValidate
        onSubmit={handleSubmit}
        className="flex flex-col gap-6 rounded-xl border border-gray-200 bg-white p-6"
      >
        {!data.managedByPalomma && (
          <>
            <WebhookEndpointForm
              url={url}
              error={urlError}
              disabled={!canEdit}
              onUrlChange={handleUrlChange}
            />
            {canEdit &&
              (data.configured ? (
                <WebhookSecretRow secretPreview={data.secretPreview} />
              ) : (
                <p className="text-sm text-gray-600">
                  Generaremos tu secreto cuando guardes la URL.
                </p>
              ))}
          </>
        )}

        <div className="flex flex-col gap-3">
          <div>
            <h3 className="text-base font-semibold">Eventos</h3>
            <p className="text-sm text-gray-600">
              {canEdit
                ? "Elige qué eventos quieres recibir. Abre cada uno para ver un ejemplo de lo que te enviamos."
                : "Estos son los eventos que recibe tu sistema. Abre cada uno para ver un ejemplo."}
            </p>
            {data.crmIdRequired && (
              <p className="mt-1 text-sm text-gray-600">
                Solo enviamos eventos de leads que ya tienen un ID de tu CRM, salvo «Lead creado».
              </p>
            )}
          </div>
          <WebhookEventList
            catalog={data.catalog}
            events={data.events}
            selected={selectedEvents}
            disabled={!canEdit}
            onToggle={handleToggleEvent}
          />
        </div>

        {canEdit && (
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-end">
            {selectedEvents.length === 0 && (
              <p className="text-sm text-gray-600">
                Sin eventos seleccionados no te enviaremos nada.
              </p>
            )}
            <Button
              type="submit"
              className="min-h-11 w-full md:w-auto"
              disabled={!isDirty || !url.trim() || update.isLoading}
            >
              {update.isLoading ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        )}
      </form>

      {canEdit && (
        <WebhookTestPanel
          catalog={data.catalog}
          events={data.events}
          disabledReason={testDisabledReason}
        />
      )}
    </div>
  );
};

export default WebhooksConfiguration;
